import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  type RangeSelection,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical';

import {
  type FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { Link } from '@dr.pogodin/react-utils';

import {
  $isListNode,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createHeadingNode, $isHeadingNode } from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import { mergeRegister } from '@lexical/utils';

import IconList from 'assets/images/icon-list-625px.svg';
import IconUndo from 'assets/images/icon-undo-24px.svg';

import S from './style.scss';

function isHeading(selection: RangeSelection): boolean {
  for (const node of selection.getNodes()) {
    if (
      !$isHeadingNode(node)
      && !node.getParents().some((n) => $isHeadingNode(n))
    ) return false;
  }

  return true;
}

function isListBlock(selection: RangeSelection): boolean {
  for (const node of selection.getNodes()) {
    if (
      node.getType() !== 'list'
      && !node.getParents().some((n) => $isListNode(n))
    ) {
      return false;
    }
  }

  return true;
}

const Toolbar: FunctionComponent = () => {
  const [editor] = useLexicalComposerContext();

  const [canRedo, setCanRedo] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isH2, setIsH2] = useState(false);
  const [isList, setIsList] = useState(false);

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsH2(isHeading(selection));
      setIsList(isListBlock(selection));
    }
  }, []);

  useEffect(() => mergeRegister(
    editor.registerUpdateListener(({ editorState }) => {
      editorState.read(
        () => {
          $updateToolbar();
        },
        { editor },
      );
    }),
    editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (/* payload, newEditor */) => {
        $updateToolbar();
        return false;
      },
      COMMAND_PRIORITY_LOW,
    ),
    editor.registerCommand(
      CAN_REDO_COMMAND,
      (payload) => {
        setCanRedo(payload);
        return false;
      },
      COMMAND_PRIORITY_LOW,
    ),
    editor.registerCommand(
      CAN_UNDO_COMMAND,
      (payload) => {
        setCanUndo(payload);
        return false;
      },
      COMMAND_PRIORITY_LOW,
    ),
  ), [editor, $updateToolbar]);

  return (
    <div styleName="container">
      <Link
        // disabled={!canUndo}
        onClick={() => {
          editor.dispatchCommand(UNDO_COMMAND, undefined);
        }}
        styleName="button"
        to=""
      >
        <IconUndo className={canUndo ? undefined : S.disabledIcon} />
      </Link>
      <Link
        // disabled={!canRedo}
        onClick={() => {
          editor.dispatchCommand(REDO_COMMAND, undefined);
        }}
        styleName="button mirrored"
        to=""
      >
        <IconUndo className={canRedo ? undefined : S.disabledIcon} />
      </Link>
      <Link
        className={`${S.button} ${S.bold} ${isBold ? '' : S.inactive}`}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
        }}
        to=""
      >
        B
      </Link>
      <Link
        className={`${S.button} ${S.italic} ${isItalic ? '' : S.inactive}`}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
        }}
        to=""
      >
        It
      </Link>
      <Link
        className={`${S.button} ${isH2 ? '' : S.inactive}`}
        onClick={() => {
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              if (isH2) {
                $setBlocksType(selection, () => $createParagraphNode());
              } else {
                $setBlocksType(selection, () => $createHeadingNode('h2'));
              }
            }
          });
        }}
        to=""
      >
        H2
      </Link>
      <Link
        className={`${S.button} ${isList ? '' : S.inactive}`}
        onClick={() => {
          if (isList) {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
          } else {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
          }
        }}
        to=""
      >
        <IconList
          className={isList ? undefined : S.inactive}
          height={20}
          width={20}
        />
      </Link>
    </div>
  );
};

export default Toolbar;
